import type { ActivitySearchResult, DepGraph } from "../../../models";
import type { PendingDurationEdit } from "../../../models/pullPlan";
import { type ExternalCandidate } from "../../pull-plan/depCore/DepChipTypeahead/DepChipTypeahead";
import { rowFinish, rowStart } from "../../pull-plan/depCore/graphRows";

export type BoardDates = Map<
  string,
  { start: string | null; finish: string | null }
>;

/** Apply a duration change to the staged map. Setting a duration back to its
 *  committed value CLEARS the staged edit, so a no-op never reaches the
 *  forward-pass preview or the workbench. An unknown committed value (null /
 *  undefined) always stages. Returns a new map; never mutates `prev`. */
export function stageDurationEdit(
  prev: Map<number, number>,
  activityId: number,
  days: number,
  committedDays: number | null | undefined,
): Map<number, number> {
  const next = new Map(prev);
  if (committedDays != null && days === committedDays) next.delete(activityId);
  else next.set(activityId, days);
  return next;
}

/** Snapshot every activity's current (committed) dates — the "Board dates"
 *  baseline frozen when the editor opens. */
export function captureBoardDates(graph: DepGraph): BoardDates {
  const m: BoardDates = new Map();
  for (const [id, a] of Object.entries(graph.activities)) {
    // Capture the RETAINED (drive) span, never the display span. `start` is the
    // ACTUAL start of a started activity and `finish` is the ACTUAL finish of a
    // completed one — board mode forces drive_* onto these, collapsing the
    // strip's remaining-work / driving position. drive_start/drive_finish are the
    // retained CPM points the schedule actually drives off: a completed
    // activity's is its held early finish (an out-of-sequence completion stays at
    // its logic position, not its actual finish), matching the live forward pass.
    m.set(id, { start: rowStart(a), finish: rowFinish(a) });
  }
  return m;
}

/** The staged duration map as the draft's `pending_duration_edits` array. */
export function toPendingDurationEdits(
  map: Map<number, number>,
): PendingDurationEdit[] {
  return Array.from(map, ([activity_id, duration_days]) => ({
    activity_id,
    duration_days,
  }));
}

/** Map raw activity-search hits to tie candidates for the "+ add" pickers,
 *  dropping any already in this section's scope (no self-ties). */
export function toExternalCandidates(
  results: ActivitySearchResult[],
  inScopeIds: Set<string>,
): ExternalCandidate[] {
  return results
    .filter((r) => !inScopeIds.has(String(r.id)))
    .map((r) => ({
      externalActivityId: r.id,
      taskCode: r.task_code,
      taskName: r.prefix || r.task_name,
      workCode: r.work_code,
      wbsName: r.section_name,
      startIso: r.start,
      finishIso: r.finish,
    }));
}
